import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'router-v6'
import { useSidebar } from '../../primitives/sidebar'

// Helper to check if sidebar view type is secondary nav
const isSecondaryNavType = (viewType: string) => viewType !== 'main'

// Helper function to determine sidebar view type from pathname.
export const getSidebarViewType = (pathname: string) => {
  if (pathname.startsWith('/analytics')) {
    return 'reports'
  }

  if (
    pathname.startsWith('/conversations') &&
    !pathname.startsWith('/conversations/active') &&
    !pathname.startsWith('/conversations/unassigned') &&
    !pathname.startsWith('/conversations/tasks/') &&
    !pathname.startsWith('/conversations/new/') &&
    pathname !== '/conversations/active/c' &&
    pathname !== '/conversations/unassigned/c'
  ) {
    return 'conversations'
  }

  return 'main'
}

// Helper function to check if current page is a detail page
export const isDetailPage = (pathname: string): boolean => {
  return (
    pathname.includes('/agent/builder/') ||
    pathname.includes('/flow/builder/') ||
    pathname.match(/^\/agent\/flow-runs\/[^/]+\/[^/]+$/) !== null
  )
}

// Custom hook for sidebar collapse behavior
export const useSidebarCollapseEffects = () => {
  const location = useLocation()
  const { setOpen, open } = useSidebar()
  const previousPathRef = useRef('')
  const userManuallyExpandedRef = useRef(false)
  const previousOpenRef = useRef(open)
  const autoCollapseTimeoutRef = useRef<number | null>(null)

  // Effect for detecting manual expansion - runs immediately when open changes
  useEffect(() => {
    const pathname = location.pathname
    const isDetail = isDetailPage(pathname)
    const wasOpen = previousOpenRef.current

    // Detect manual expansion: user clicked to open sidebar on detail page
    if (isDetail && open && !wasOpen) {
      userManuallyExpandedRef.current = true
      // Cancel any pending auto-collapse
      if (autoCollapseTimeoutRef.current) {
        window.clearTimeout(autoCollapseTimeoutRef.current)
        autoCollapseTimeoutRef.current = null
      }
    }

    previousOpenRef.current = open
  }, [open])

  // Effect for handling navigation-based sidebar changes
  useEffect(() => {
    const pathname = location.pathname
    const isDetail = isDetailPage(pathname)
    const previousPath = previousPathRef.current
    const wasOnDetailPage = isDetailPage(previousPath)

    // Clear any pending auto-collapse timeout
    if (autoCollapseTimeoutRef.current) {
      window.clearTimeout(autoCollapseTimeoutRef.current)
      autoCollapseTimeoutRef.current = null
    }

    // Auto-expand when navigating FROM detail page TO non-detail page
    if (wasOnDetailPage && !isDetail && !userManuallyExpandedRef.current) {
      setOpen(true)
    }

    // Auto-collapse when navigating TO detail page (and sidebar is open)
    // Check current open state from ref to avoid dependency
    if (isDetail && !userManuallyExpandedRef.current && previousOpenRef.current) {
      autoCollapseTimeoutRef.current = window.setTimeout(() => {
        if (!userManuallyExpandedRef.current) {
          setOpen(false)
        }
        autoCollapseTimeoutRef.current = null
      }, 0)
    }

    // Reset manual expansion flag when leaving detail pages
    if (!isDetail) {
      userManuallyExpandedRef.current = false
    }

    previousPathRef.current = pathname

    // Cleanup timeout on unmount
    return () => {
      if (autoCollapseTimeoutRef.current) {
        window.clearTimeout(autoCollapseTimeoutRef.current)
      }
    }
  }, [location.pathname, setOpen])
}

/**
 * Hook to manage sidebar transitions and state during navigation.
 * Handles: secondary nav collapse, detail page collapse, and transition disabling.
 */
export const useSidebarTransition = ({ inlineReports = false } = {}) => {
  const location = useLocation()
  const { setOpen, open } = useSidebar()
  const prevIsSecondaryNavRef = useRef<boolean | null>(null)
  const prevPathRef = useRef<string | null>(null)
  const disableTransitionRef = useRef(false)
  const userManuallyExpandedRef = useRef(false)
  const previousOpenRef = useRef(open)

  const viewType = getSidebarViewType(location.pathname)
  // A main sidebar that lists the reports itself has no reports panel to open.
  const sidebarViewType = inlineReports && viewType === 'reports' ? 'main' : viewType
  const isSecondaryNav = isSecondaryNavType(sidebarViewType)
  const currentIsDetailPage = isDetailPage(location.pathname)

  // Detect context switch (secondary nav <-> main nav)
  const isContextSwitch =
    prevIsSecondaryNavRef.current !== null && prevIsSecondaryNavRef.current !== isSecondaryNav

  // Detect detail page transition (to or from detail page)
  const prevIsDetailPage = prevPathRef.current ? isDetailPage(prevPathRef.current) : false
  const isDetailPageTransition = currentIsDetailPage !== prevIsDetailPage

  // Disable transitions for context switches or detail page transitions
  if (isContextSwitch || isDetailPageTransition) {
    disableTransitionRef.current = true
  }

  const disableTransition = disableTransitionRef.current

  // Detect manual expansion on detail pages and secondary nav pages
  useEffect(() => {
    const wasOpen = previousOpenRef.current

    // User manually expanded sidebar on detail page or secondary nav page
    userManuallyExpandedRef.current ||= (currentIsDetailPage || isSecondaryNav) && open && !wasOpen

    previousOpenRef.current = open
  }, [open, currentIsDetailPage, isSecondaryNav])

  // Handle all sidebar open/close state changes
  useLayoutEffect(() => {
    const wasSecondaryNav = prevIsSecondaryNavRef.current
    const wasDetailPage = prevPathRef.current ? isDetailPage(prevPathRef.current) : false

    // Detect transition TO secondary nav (not already on secondary nav)
    const transitioningToSecondaryNav = isSecondaryNav && !wasSecondaryNav

    // Update refs
    prevIsSecondaryNavRef.current = isSecondaryNav
    prevPathRef.current = location.pathname

    // Secondary nav: only auto-collapse when FIRST entering (transitioning to secondary nav)
    // Don't collapse if user manually expanded it
    if (transitioningToSecondaryNav && !userManuallyExpandedRef.current) {
      setOpen(false)
    }
    // Coming back from secondary nav: expand (unless user manually collapsed it)
    else if (wasSecondaryNav && !isSecondaryNav && !userManuallyExpandedRef.current) {
      setOpen(true)
    }
    // Navigating TO detail page: collapse (unless manually expanded)
    else if (currentIsDetailPage && !wasDetailPage && !userManuallyExpandedRef.current) {
      setOpen(false)
    }
    // Navigating FROM detail page: expand (unless manually expanded)
    else if (!currentIsDetailPage && wasDetailPage && !userManuallyExpandedRef.current) {
      setOpen(true)
    }

    // Reset manual expansion flag when leaving detail pages and secondary nav
    if (!currentIsDetailPage && !isSecondaryNav) {
      userManuallyExpandedRef.current = false
    }

    // Reset disableTransition after paint
    if (disableTransitionRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          disableTransitionRef.current = false
        })
      })
    }
  }, [isSecondaryNav, setOpen, location.pathname, currentIsDetailPage])

  return { disableTransition, sidebarViewType, isSecondaryNav }
}
